// Types
import type { ReactNode } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import Image from 'next/image';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import useCurrentSession from '@/utils/hooks/user/useCurrentSession';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

// Routes
import { SIGN_IN } from '@/lib/routes/publicRoutes';
// Lib
import { reassuranceLogos, reassuranceLogosAwards } from '@/lib/signup/reassuranceLogos';

import Divider from '@/components/base/Divider/Divider';
import { NO_BACKGROUND_BLACK } from '@/components/base/Dropdown';
// Components
import H1 from '@/components/base/Headings/H1';
import LinkBase from '@/components/base/LinkBase';
import LoaderPage from '@/components/base/Loader/LoaderPage';
import Iconosquare from '@/components/icons/Iconosquare';
import SwitchLanguage from '@/components/pages/signup/SwitchLanguage';
import { Trans } from '@/components/tools/translation';

// Images
import illustration from './../../../public/images/signup/signup-layout-illustration.webp';

type LayoutSignUpPublicProps = {
    children: ReactNode;
    titleLocale: string;
    subTitleLocale?: string;
    headerChildren?: ReactNode;
    displayFooter?: boolean;
};

const LayoutSignUpPublic = ({
    children,
    titleLocale,
    subTitleLocale,
    headerChildren,
    displayFooter = true
}: LayoutSignUpPublicProps) => {
    const { t } = useTranslation('signup_funnel');
    const { user, isLoading } = useCurrentSession({ isLoggedRedirect: true });
    const { useRemoveDarkMode } = useDarkMode();

    useRemoveDarkMode();
    if (isLoading || user) {
        return <LoaderPage />;
    }

    return (
        <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY}>
            <div className="relative bg-white">
                <div className="absolute inset-0 z-0 h-screen w-screen overflow-hidden bg-icoSignUpLayer" />
                <div className="scrollbar relative h-screen">
                    <div className="flex flex-row justify-between px-10 pb-6 pt-10 md:px-15 md:pb-10 md:pt-15">
                        <Iconosquare size={36} className="text-icoBlue md:w-9" />
                        <SwitchLanguage buttonStyle={NO_BACKGROUND_BLACK} />
                    </div>
                    <div className="mx-auto flex max-w-screen-2xl flex-col pb-20 text-icoBlack sm:pb-10 md:flex-row">
                        <div className="relative p-10 md:w-1/2 md:p-15">
                            <div className="relative w-full md:mx-auto md:max-w-[350px]">
                                {headerChildren}
                                <H1 className="mb-6">
                                    <Trans
                                        i18nKey={'signup_funnel:create_account.' + titleLocale}
                                        components={{ br: <br /> }}
                                    />
                                </H1>
                                {subTitleLocale ? (
                                    <p className="mb-10 font-GMedium text-icoGray-500">
                                        <Trans
                                            i18nKey={
                                                'signup_funnel:create_account.' + subTitleLocale
                                            }
                                            components={{ br: <br /> }}
                                        />
                                    </p>
                                ) : null}
                                {children}
                                {displayFooter ? (
                                    <>
                                        <Divider className="my-8">
                                            <span className="text-sm text-icoGray-400">
                                                {t('commons:or')}
                                            </span>
                                        </Divider>
                                        <div className="text-center font-GSemiBold text-sm">
                                            <Trans
                                                i18nKey="signup_funnel:create_account.already_have_account"
                                                components={{
                                                    tag0: (
                                                        <LinkBase
                                                            key="login"
                                                            href={SIGN_IN}
                                                            className="text-icoBlue"
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                        <div className="relative flex flex-col items-center px-10 md:h-full md:w-1/2 md:pl-0 md:pr-16">
                            <Image
                                alt=""
                                width={672}
                                height={510}
                                src={illustration}
                                className="w-full max-w-2xl"
                                placeholder="blur"
                            />
                            <div className="mt-10 flex flex-col items-center space-y-5">
                                <ul className="flex w-fit justify-center gap-3">
                                    {reassuranceLogosAwards.map((logo) => (
                                        <li key={logo.alt}>
                                            <Image
                                                alt={logo.alt}
                                                src={logo.image}
                                                height={80}
                                                placeholder="blur"
                                            />
                                        </li>
                                    ))}
                                </ul>
                                <ul className="flex w-fit justify-center gap-3">
                                    {reassuranceLogos.map((logo) => (
                                        <li key={logo.alt}>
                                            <Image
                                                alt={logo.alt}
                                                src={logo.image}
                                                height={45}
                                                placeholder="blur"
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </GoogleReCaptchaProvider>
    );
};

export default LayoutSignUpPublic;
